import React from 'react';
import { Link } from 'react-router-dom';
import { formatUTCDate } from '../../utils/utils';
import StatusPill from '../users/StatusPill';
import UserDetailLink from '../common/UserDetailLink';

/**
 * Survey responses list table (feature 052). Uses the shared au-table styling so it looks
 * identical to the other admin list pages (horizontal scroll on narrow screens).
 *
 * The member's name used to BE the control that opened the response — clicking it was the only
 * way in, and nothing on the row said so. **View** is a column of its own at the end of the row
 * now, where a table's actions are looked for, and the name links to the member's User 360 page
 * (`/user-detail/:encodedUserId`) — styled as a link, so it no longer hides what it does. A
 * response with no member behind it keeps the name as plain text.
 *
 * View is a real <Link> to `/survey-response/:id`, not a handler — the response is a page now,
 * so it must be middle-clickable, right-click-copyable and openable in a new tab like any other
 * link in the portal. `onRowClick` is gone; the table needs no callback for it.
 *
 * `Free trial granted` carries the length of the LATEST grant with it — "Yes - 15 Days",
 * "Yes - Lifetime" — worded by the API so both survey tables read identically, and a bare
 * "Yes" when we have no length for it. It named every grant the member had ever had for a
 * few weeks; the column is about where they stand now, and the full history is on
 * /free-grants.
 *
 * `Subscription` is the /users status pill — the SAME component, reading the same
 * `subscription_state` / `trial_days_remaining` the members list reads, so a member's plan
 * cannot mean one thing on one screen and something else here. It sits ahead of the grant
 * columns because it is what an operator checks before granting: someone already paying does
 * not need the three months this survey promised.
 *
 * `Grant Free Access` is the action those two columns lead to (065). This survey promises the
 * responder three months and nothing applies it automatically, so the screen that shows who has
 * been paid is also the screen that pays them. `onGrantAccess` is optional — without it the
 * column is not rendered at all.
 */
export default function SurveyResponsesTable({ items, onGrantAccess }) {
  const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

  // Whether we ever granted this member free access. Arrives as a PG boolean; tolerate the
  // 1/0 and 't' shapes too. Every grant counts, revoked and expired included — the same
  // question the /users "Last provided" cell answers.
  const granted = (it) => {
    const v = it.free_access_granted;
    return v === true || v === 1 || v === '1' || v === 't' || v === 'true';
  };

  // "Yes - 15 Days" — the LATEST grant only, inside the badge rather than in a column of its
  // own. It briefly named every reward the member had ever had ("Yes - 15 Days, 90 Days"); a
  // column asked to say what a member is on now was answering with their history, and the
  // oldest grant read as current to anyone scanning the list. `free_access_duration` is the
  // most recent grant by `created_at DESC, id DESC` — the same row `free_access_granted_at`
  // dates, so the length and the date in the next column always describe one grant.
  //
  // NOT `free_access_duration_all`: it is still on the API and in the CSV, and the whole
  // history is read on /free-grants. Falling back to it here would print an older length
  // under a column that promises the newest.
  //
  // A grant we know no length for stays a bare "Yes"; it is never padded with a made-up one.
  const grantedLabel = (it) => (it.free_access_duration ? `Yes - ${it.free_access_duration}` : 'Yes');

  return (
    <div className="au-table-wrap">
      <table className="au-table sr-responses-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Submitted</th>
            <th>Survey</th>
            <th>Subscription</th>
            <th>Free trial granted</th>
            <th>Trial provided on</th>
            {onGrantAccess && <th>Grant Free Access</th>}
            <th>Satisfaction</th>
            <th>Recommend</th>
            <th>View</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.response_id}>
              <td>
                {it.user_id ? (
                  <Link
                    className="au-link"
                    to={`/user-detail/${btoa(String(it.user_id))}`}
                    title="Open user profile"
                    data-testid="user-detail-link"
                  >
                    {it.name || `User #${it.user_id}`}
                  </Link>
                ) : (
                  <span className="sr-user">{it.name || '—'}</span>
                )}
              </td>
              <td><UserDetailLink userId={it.user_id} fallback={dash(it.email)} testId="user-detail-email-link">{it.email}</UserDetailLink></td>
              <td>{formatUTCDate(it.submitted_at)}</td>
              <td>
                {/* 105: which survey this response came from — the two ask the same questions
                    but promise different rewards (3 months vs 30 days). */}
                {it.survey_label ? (
                  <span
                    className={`sr-survey ${it.survey_code === 'help_us_improve_30_days' ? 'sr-survey--30' : 'sr-survey--3m'}`}
                    data-testid="sr-survey-badge"
                  >
                    {it.survey_label}
                  </span>
                ) : '—'}
              </td>
              <td>
                <StatusPill state={it.subscription_state} trialDays={it.trial_days_remaining} />
              </td>
              <td>
                <span className={`sr-grant ${granted(it) ? 'sr-grant--yes' : 'sr-grant--no'}`}>
                  {granted(it) ? grantedLabel(it) : 'No'}
                </span>
              </td>
              <td>{it.free_access_granted_at ? formatUTCDate(it.free_access_granted_at) : '—'}</td>
              {onGrantAccess && (
                <td>
                  {/* No member behind the response, nobody to grant. The other columns still
                      render — the answers are worth reading either way. */}
                  {it.user_id ? (
                    <button
                      type="button"
                      className="fg-row-action"
                      onClick={() => onGrantAccess(it)}
                      data-testid="grant-free-access"
                    >
                      Grant Free Access
                    </button>
                  ) : '—'}
                </td>
              )}
              <td>{dash(it.satisfaction)}</td>
              <td>{dash(it.recommend)}</td>
              <td>
                <Link
                  className="sr-view-btn"
                  to={`/survey-response/${it.response_id}`}
                  data-testid="view-response"
                >
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
